
import { useState } from 'react';

const ServiceAreaSection = () => {
  const [activeZipcode, setActiveZipcode] = useState<string | null>(null);

  // Group zipcodes by area with full names for SEO
  const zipcodeGroups = [
    { 
      area: 'Aberdeen & Bel Air', 
      codes: [
        { zip: '21001', name: 'Aberdeen' },
        { zip: '21009', name: 'Abingdon' },
        { zip: '21010', name: 'Bel Air' },
        { zip: '21014', name: 'Bel Air' },
        { zip: '21015', name: 'Bel Air' },
        { zip: '21017', name: 'Belcamp' }
      ]
    },
    { 
      area: 'Edgewood & Joppa', 
      codes: [
        { zip: '21040', name: 'Edgewood' },
        { zip: '21085', name: 'Joppa' },
        { zip: '21087', name: 'Kingsville' }
      ]
    },
    { 
      area: 'Fallston & Forest Hill', 
      codes: [
        { zip: '21047', name: 'Fallston' },
        { zip: '21050', name: 'Forest Hill' }
      ]
    },
    { 
      area: 'Havre de Grace', 
      codes: [
        { zip: '21078', name: 'Havre de Grace' },
        { zip: '21082', name: 'Hydes' },
        { zip: '21084', name: 'Jarrettsville' }
      ]
    },
    { 
      area: 'Jarrettsville & Street', 
      codes: [
        { zip: '21154', name: 'Street' },
        { zip: '21161', name: 'White Hall' }
      ]
    },
    { 
      area: 'Other Harford County Areas', 
      codes: [
        { zip: '21005', name: 'Baldwin' },
        { zip: '21013', name: 'Baldwin' },
        { zip: '21018', name: 'Churchville' },
        { zip: '21028', name: 'Churchville' },
        { zip: '21034', name: 'Darlington' },
        { zip: '21111', name: 'Perryman' },
        { zip: '21130', name: 'Pylesville' },
        { zip: '21132', name: 'Pylesville' },
        { zip: '21160', name: 'Whiteford' }
      ]
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-green-50/30 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
            Proudly Serving <span className="text-[#24820C]">Harford County</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Professional landscaping and property management services across 25+ Maryland communities
          </p>
        </div>

        {/* Map Container */}
        <div className="mb-12 rounded-lg overflow-hidden shadow-2xl">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d197094.89347891!2d-76.41!3d39.55!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c7f1f8f8f8f8f8%3A0x1234567890abcdef!2sHarford%20County%2C%20MD!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
            width="100%"
            height="500"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Service Area Map - Harford County, MD"
          ></iframe>
        </div>

        {/* Zipcode Grid with Area Names */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Service Area Zipcodes & Communities
          </h3>
          
          <div className="space-y-8">
            {zipcodeGroups.map((group, groupIndex) => (
              <div key={groupIndex}>
                <h4 className="text-lg font-bold text-[#24820C] mb-4 flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  {group.area}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {group.codes.map((item) => (
                    <div
                      key={item.zip}
                      onMouseEnter={() => setActiveZipcode(item.zip)}
                      onMouseLeave={() => setActiveZipcode(null)}
                      className={`
                        relative p-4 rounded-lg text-center
                        transition-all duration-300 cursor-pointer
                        ${activeZipcode === item.zip 
                          ? 'bg-[#24820C] text-white shadow-lg scale-105' 
                          : 'bg-green-50 text-gray-800 hover:bg-green-100'
                        }
                      `}
                    >
                      <div className="font-bold text-lg mb-1">{item.zip}</div>
                      <div className={`text-sm ${activeZipcode === item.zip ? 'text-green-100' : 'text-gray-600'}`}>
                        {item.name}
                      </div>
                      {activeZipcode === item.zip && (
                        <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#83B328] rounded-full flex items-center justify-center">
                          <i className="ri-check-line text-white text-sm"></i>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-[#24820C] to-[#83B328] rounded-lg p-8 text-white">
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Don't See Your Zipcode?
          </h3>
          <p className="text-lg mb-6 max-w-2xl mx-auto">
            We're always expanding! Contact us at <strong>(443) 618-5890</strong> to discuss your project. 
            We may be able to accommodate your area.
          </p>
          <a 
            href="#"
            className="call-trigger inline-block bg-white text-[#24820C] hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg text-lg transition-colors whitespace-nowrap cursor-pointer"
            data-phone="4436185890"
          >
            <i className="ri-phone-line mr-2"></i>
            Call Us Now
          </a>
        </div>
      </div>
    </section>
  );
};

export default ServiceAreaSection;
